/**
 * Hooks de TanStack Query para Gurus, disponibilidad y reservas.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';

/**
 * Columnas de guru_profiles que puede leer un visitante sin sesión (anon).
 * Desde la migración del 28/10/2026 anon solo tiene GRANT sobre estas
 * columnas: un select('*') sobre guru_profiles en una página pública falla.
 * Quedan fuera (datos internos): calendar_email, calendar_provider,
 * calendar_connected_at, payout_method, commission_percent, approved_by,
 * terms_accepted_at, terms_version, late_cancels_90d, late_cancels_total,
 * paused_for_reliability_at, pending_payout_adjustment_cents,
 * is_excluded_from_premium_credit.
 */
// Un solo literal (sin concatenar) para que supabase-js pueda tipar el select.
export const GURU_PUBLIC_COLUMNS = 'user_id, title, academic_title, extended_bio, intro_video_url, intro_video_vertical_url, years_experience, languages, countries_served, session_price_usd_cents, session_duration_min, calendar_connected, avg_rating, review_count, completed_sessions, is_approved, approved_at, is_public, accepts_new_bookings, min_booking_notice_hours, max_booking_horizon_days, buffer_between_sessions_min, shelf_rank, created_at, updated_at';

// =====================================================================
// Tipos
// =====================================================================

export interface GuruProfile {
  user_id: string;
  /**
   * Formación académica del guru. El titular comercial es
   * `profile.headline`. (Reemplaza a `title`, que duplicaba el headline y
   * no se publicaba en ninguna pantalla.)
   */
  academic_title: string | null;
  extended_bio: string | null;
  /** Video de presentación (YouTube/Vimeo). Lo carga el admin. */
  intro_video_url: string | null;
  /** Versión vertical (Short) para el celular. Lo carga el admin. */
  intro_video_vertical_url: string | null;
  years_experience: number | null;
  languages: string[];
  countries_served: string[];
  session_price_usd_cents: number;
  session_duration_min: number;
  /** No viene en lecturas públicas (ver GURU_PUBLIC_COLUMNS). */
  commission_percent?: number;
  calendar_connected: boolean;
  avg_rating: number;
  review_count: number;
  completed_sessions: number;
  is_approved: boolean;
  is_public: boolean;
  accepts_new_bookings: boolean;
  // De profiles (join)
  profile?: {
    full_name: string;
    handle: string | null;
    avatar_url: string | null;
    headline: string | null;
    country_code: string | null;
  };
  // De guru_specialties (join via assignments) — taxonomía vieja, se
  // mantiene durante el período de transición para no romper el
  // directorio público que aún filtra por specialty_slug.
  specialties?: Array<{ id: string; slug: string; name: string }>;
  // De course_categories (join via guru_category_assignments) — taxonomía
  // unificada. Las páginas de perfil del Guru muestran esto en
  // "Especialidades / En qué te puede ayudar" — y es lo que mapea
  // con los cursos del catálogo para el bridge curso → guru.
  categories?: Array<{ id: string; slug: string; name: string }>;
  // De expertise_topics (join via guru_topics) — expertise fina
  // (migración 20260801000001). core = profundo (máx 5, ordenados por la
  // relevancia que el guru les dio), secundario = "también orienta en".
  // Solo los core generan la recomendación curso → guru.
  topics?: Array<{
    id: string;
    slug: string;
    name: string;
    category_id: string;
    level: 'core' | 'secundario';
    sort_order: number;
  }>;
  // De industries (join via guru_industries) — sectores en los que ha
  // operado. Facet solo del guru: buscador + perfil, no toca el match.
  industries?: Array<{ id: string; slug: string; name: string }>;
}

export interface GuruAvailability {
  id: string;
  guru_id: string;
  day_of_week: number; // 0=Dom, 6=Sáb
  start_time: string;  // 'HH:MM:SS'
  end_time: string;
  timezone: string;
  is_active: boolean;
}

export interface GuruSpecialty {
  id: string;
  slug: string;
  name: string;
}

// =====================================================================
// Hooks
// =====================================================================

/**
 * Lista pública de Gurus aprobados que aceptan reservas.
 *
 * Filtros soportados:
 *   * search        — buscador con estrategia "tiered" (ver más abajo).
 *   * category_slug — slug de course_categories (taxonomía unificada).
 *   * specialty_slug — slug de guru_specialties (taxonomía vieja, se
 *                     mantiene por backward compat hasta deprecar).
 *   * language      — código ISO ('es', 'en', 'pt').
 *
 * Buscador tiered (decisión Marco):
 *   1. Primero busca en CAMPOS CORTOS (nombre, handle, titular, formación).
 *      Si encuentra al menos 1 match, devuelve solo esos.
 *   2. Si NO hubo matches en campos cortos, expande la búsqueda al
 *      `extended_bio` (texto largo del perfil). Así si alguien escribe
 *      "ABM" y nadie lo tiene en el headline pero un guru lo menciona
 *      en su bio, igual aparece.
 *
 *   Esto evita ruido en queries comunes (un guru que solo menciona
 *   "marketing" de paso en una bio larga no debería aparecer cuando
 *   buscas "marketing") pero rescata búsquedas específicas cuando los
 *   chips de categoría no alcanzan.
 */
/**
 * Los gurus del hero de la landing, en UN solo round trip (RPC get_hero_gurus).
 *
 * Existe porque useGurus hace 5 queries encadenadas (perfiles, specialties,
 * categorias, ratings...) y el hero solo necesita cara + nombre + titular:
 * esperar la cascada completa era el shimmer largo del primer render.
 * staleTime alto a proposito: la lista de caras cambia poco y no vale la pena
 * re-pedirla en cada visita a la landing dentro de la misma sesion.
 */
export interface HeroGuru {
  user_id: string;
  profile: {
    full_name: string | null;
    headline: string | null;
    avatar_url: string | null;
  };
}

export function useHeroGurus(limit = 12) {
  return useQuery({
    queryKey: ['hero-gurus', limit],
    queryFn: async (): Promise<HeroGuru[]> => {
      const { data, error } = await supabase.rpc('get_hero_gurus', { _limit: limit });
      if (error) throw error;
      return ((data ?? []) as any[]).map((r) => ({
        user_id: r.user_id,
        profile: { full_name: r.full_name, headline: r.headline, avatar_url: r.avatar_url },
      }));
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useGurus(filters: {
  search?: string;
  category_slug?: string;
  /** @deprecated usar category_slug. Se mantiene por backward compat. */
  specialty_slug?: string;
  language?: string;
} = {}) {
  return useQuery({
    queryKey: ['gurus', filters],
    queryFn: async (): Promise<GuruProfile[]> => {
      // Query 1: guru_profiles solo (sin join, porque PostgREST no puede
      // inferir relación guru_profiles → profiles ya que ambas cuelgan
      // de auth.users sin FK directa entre ellas).
      let query = supabase
        .from('guru_profiles')
        .select(GURU_PUBLIC_COLUMNS)
        .eq('is_public', true)
        .eq('is_approved', true)
        .eq('accepts_new_bookings', true)
        .order('avg_rating', { ascending: false });

      if (filters.language) {
        query = query.contains('languages', [filters.language]);
      }

      const { data: gurusRaw, error } = await query;
      if (error) throw error;

      const guruIds = (gurusRaw ?? []).map((g: any) => g.user_id);
      if (guruIds.length === 0) return [];

      // Query 2: profiles correspondientes
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, handle, avatar_url, headline, country_code')
        .in('id', guruIds);

      // Map de id → fila de profile (shape definido por el .select() de arriba).
      // No tipamos más estricto porque ese .select() trae solo campos básicos
      // y otros consumers de gurus.ts esperan shapes ligeramente distintos.
      const profilesMap: Record<string, { id: string; full_name: string | null; handle: string | null; avatar_url: string | null; headline: string | null; country_code: string | null }> = {};
      for (const p of (profiles ?? [])) {
        profilesMap[p.id] = p;
      }

      // Query 3a: specialty_assignments (taxonomía vieja, backward compat)
      const { data: assignments } = await supabase
        .from('guru_specialty_assignments')
        .select('guru_id, specialty:guru_specialties(id, slug, name)')
        .in('guru_id', guruIds);

      const specialtiesMap: Record<string, GuruSpecialty[]> = {};
      for (const a of ((assignments ?? []) as any[])) {
        if (!a.specialty) continue;
        const list = specialtiesMap[a.guru_id] ?? [];
        list.push(a.specialty);
        specialtiesMap[a.guru_id] = list;
      }

      // Query 3b: category_assignments (taxonomía unificada — la nueva).
      // Estas son las mismas categorías que tienen los cursos, lo que
      // permite que los chips del directorio matcheen 1:1 con el
      // catálogo y que el bridge curso → guru funcione.
      const { data: catAssignments } = await supabase
        .from('guru_category_assignments')
        .select('guru_id, category:course_categories(id, slug, name)')
        .in('guru_id', guruIds);

      const categoriesMap: Record<string, Array<{ id: string; slug: string; name: string }>> = {};
      for (const a of ((catAssignments ?? []) as any[])) {
        if (!a.category) continue;
        const list = categoriesMap[a.guru_id] ?? [];
        list.push(a.category);
        categoriesMap[a.guru_id] = list;
      }

      // Query 3c: subtemas de expertise (capa fina sobre las categorías)
      const { data: topicRows } = await supabase
        .from('guru_topics')
        .select('guru_id, level, sort_order, topic:expertise_topics(id, slug, name, category_id)')
        .in('guru_id', guruIds);

      const topicsMap: Record<string, NonNullable<GuruProfile['topics']>> = {};
      for (const r of ((topicRows ?? []) as any[])) {
        if (!r.topic) continue;
        const list = topicsMap[r.guru_id] ?? [];
        list.push({ ...r.topic, level: r.level, sort_order: r.sort_order });
        topicsMap[r.guru_id] = list;
      }
      // core primero, cada nivel en el orden de relevancia del guru.
      for (const list of Object.values(topicsMap)) {
        list.sort((a, b) =>
          a.level === b.level
            ? a.sort_order - b.sort_order
            : a.level === 'core' ? -1 : 1,
        );
      }

      // Query 3d: industrias en las que ha operado
      const { data: indRows } = await supabase
        .from('guru_industries')
        .select('guru_id, industry:industries(id, slug, name)')
        .in('guru_id', guruIds);

      const industriesMap: Record<string, NonNullable<GuruProfile['industries']>> = {};
      for (const r of ((indRows ?? []) as any[])) {
        if (!r.industry) continue;
        const list = industriesMap[r.guru_id] ?? [];
        list.push(r.industry);
        industriesMap[r.guru_id] = list;
      }

      // Merge en JS
      let gurus = (gurusRaw ?? []).map((g: any) => ({
        ...g,
        profile: profilesMap[g.user_id],
        specialties: specialtiesMap[g.user_id] ?? [],
        categories: categoriesMap[g.user_id] ?? [],
        topics: topicsMap[g.user_id] ?? [],
        industries: industriesMap[g.user_id] ?? [],
      })) as GuruProfile[];

      // Filtrar solo los que tienen profile público (porque profilesMap solo
      // contiene los que la RLS permitió leer)
      gurus = gurus.filter((g) => g.profile);

      // ----- Filtros de categoría / specialty -----
      if (filters.category_slug) {
        gurus = gurus.filter((g) =>
          g.categories?.some((c) => c.slug === filters.category_slug)
        );
      }
      if (filters.specialty_slug) {
        gurus = gurus.filter((g) =>
          g.specialties?.some((s) => s.slug === filters.specialty_slug)
        );
      }

      // ----- Buscador tiered -----
      if (filters.search) {
        const q = filters.search.trim().toLowerCase();
        if (q.length > 0) {
          // Tier 1 — campos cortos / prominentes
          const tier1 = gurus.filter((g) => {
            const hay = [
              g.profile?.full_name,
              g.profile?.handle,
              g.profile?.headline,
              g.academic_title,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return hay.includes(q);
          });

          if (tier1.length > 0) {
            gurus = tier1;
          } else {
            // Tier 2 — incluye bio extendida (texto largo).
            gurus = gurus.filter((g) => {
              const hay = [
                g.profile?.full_name,
                g.profile?.handle,
                g.profile?.headline,
                g.academic_title,
                g.extended_bio,
              ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return hay.includes(q);
            });
          }
        }
      }

      return sortShelf(gurus);
    },
    // El directorio cambia poco: volver atras desde un perfil no debe
    // re-disparar toda la carga.
    staleTime: 2 * 60_000,
  });
}

/**
 * La "percha" del directorio (y de todo listado de gurus sin filtro propio):
 *   1. Agendables primero (calendar_connected): nunca arriba lo que no se
 *      puede reservar.
 *   2. shelf_rank: curaduría del admin (menor = primero), igual que
 *      featured_rank en cursos.
 *   3. avg_rating: la tracción real cuando exista.
 *   4. Rotación DIARIA determinística entre empatados: hash(user_id + fecha).
 *      Nadie queda enterrado por orden de llegada y la vitrina cambia sola
 *      cada día, para todos los visitantes igual (no por sesión).
 */
function sortShelf<T extends { user_id: string; calendar_connected?: boolean; shelf_rank?: number | null; avg_rating?: number | null }>(gurus: T[]): T[] {
  const today = new Date().toISOString().slice(0, 10);
  const daily = (id: string): number => {
    let h = 5381;
    const key = id + today;
    for (let i = 0; i < key.length; i++) h = ((h << 5) + h + key.charCodeAt(i)) | 0;
    return h >>> 0;
  };
  return [...gurus].sort((a, b) => {
    if (!!a.calendar_connected !== !!b.calendar_connected) return a.calendar_connected ? -1 : 1;
    const ra = a.shelf_rank ?? Number.MAX_SAFE_INTEGER;
    const rb = b.shelf_rank ?? Number.MAX_SAFE_INTEGER;
    if (ra !== rb) return ra - rb;
    const va = a.avg_rating ?? 0;
    const vb = b.avg_rating ?? 0;
    if (va !== vb) return vb - va;
    return daily(a.user_id) - daily(b.user_id);
  });
}

/**
 * Detalle de un Guru por handle.
 */
/**
 * Reliability score público de un Guru (Sprint 10.5).
 * Solo muestra badge si tiene >=10 sesiones Y >=95%.
 */
export interface GuruReliability {
  guru_id: string;
  completed_sessions: number;
  late_cancels_total: number;
  reliability_pct: number | null;
  show_badge: boolean;
}

export function useGuruReliability(guruId: string | undefined) {
  return useQuery({
    queryKey: ['guru-reliability', guruId],
    queryFn: async (): Promise<GuruReliability | null> => {
      if (!guruId) return null;
      const { data, error } = await supabase
        .from('guru_reliability')
        .select('*')
        .eq('guru_id', guruId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as GuruReliability | null;
    },
    enabled: !!guruId,
    staleTime: 5 * 60_000,
  });
}

export function useGuru(handle: string) {
  return useQuery({
    queryKey: ['guru', handle],
    queryFn: async (): Promise<GuruProfile | null> => {
      const { data: profile, error: pErr } = await supabase
        .from('profiles')
        .select('id, full_name, handle, avatar_url, headline, country_code')
        .eq('handle', handle)
        .maybeSingle();
      if (pErr) throw pErr;
      if (!profile) return null;

      // Todo lo demás depende SOLO de profile.id, así que va en paralelo.
      // Antes eran 5 awaits encadenados y el "Cargando…" del perfil duraba
      // 6 round trips; ahora son 2 (profiles → todo lo demás a la vez).
      const [guruRes, assignmentsRes, catAssignmentsRes, topicRowsRes, indRowsRes] = await Promise.all([
        supabase
          .from('guru_profiles')
          .select(GURU_PUBLIC_COLUMNS)
          .eq('user_id', profile.id)
          .eq('is_public', true)
          .maybeSingle(),
        // Query separada para specialties (evita el bug del triple nesting)
        supabase
          .from('guru_specialty_assignments')
          .select('specialty:guru_specialties(id, slug, name)')
          .eq('guru_id', profile.id),
        // Categorías unificadas (course_categories) — lo que muestra el perfil.
        supabase
          .from('guru_category_assignments')
          .select('category:course_categories(id, slug, name)')
          .eq('guru_id', profile.id),
        // Subtemas de expertise (core primero, en el orden del guru)
        supabase
          .from('guru_topics')
          .select('level, sort_order, topic:expertise_topics(id, slug, name, category_id)')
          .eq('guru_id', profile.id),
        // Industrias en las que ha operado
        supabase
          .from('guru_industries')
          .select('industry:industries(id, slug, name)')
          .eq('guru_id', profile.id),
      ]);

      const { data: guru, error: gErr } = guruRes;
      if (gErr) throw gErr;
      if (!guru) return null;

      const specialties = (((assignmentsRes.data ?? []) as any[]))
        .map((a) => a.specialty)
        .filter(Boolean);

      const categories = (((catAssignmentsRes.data ?? []) as any[]))
        .map((a) => a.category)
        .filter(Boolean);

      const topicRows = topicRowsRes.data;

      const topics = ((topicRows ?? []) as any[])
        .filter((r) => r.topic)
        .map((r) => ({ ...r.topic, level: r.level, sort_order: r.sort_order }))
        .sort((a, b) =>
          a.level === b.level
            ? a.sort_order - b.sort_order
            : a.level === 'core' ? -1 : 1,
        );

      const industries = ((indRowsRes.data ?? []) as any[])
        .map((r) => r.industry)
        .filter(Boolean);

      return {
        ...guru,
        profile: {
          full_name: profile.full_name,
          handle: profile.handle,
          avatar_url: profile.avatar_url,
          headline: profile.headline,
          country_code: profile.country_code,
        },
        specialties,
        categories,
        topics,
        industries,
      } as GuruProfile;
    },
    enabled: !!handle,
    staleTime: 2 * 60_000,
  });
}

// =====================================================================
// Mutations para el editor de disponibilidad (Sprint 10.2d)
// =====================================================================

export interface AvailabilityBlock {
  id?: string;            // si está presente: update; si no: insert
  day_of_week: number;    // 0=Dom, 1=Lun, ..., 6=Sáb
  start_time: string;     // "HH:MM" o "HH:MM:SS"
  end_time: string;
  timezone: string;
}

/**
 * Reemplaza TODA la disponibilidad del Guru con la lista provista.
 *
 * Estrategia: delete all + insert all (transaccional desde el cliente).
 * Más simple que diff individual de inserts/updates/deletes — y la cantidad
 * de slots por Guru es chica (típicamente 5-15 bloques).
 */
export function useReplaceMyAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (blocks: AvailabilityBlock[]) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');

      // Borrar todos los activos
      const { error: delErr } = await supabase
        .from('guru_availability')
        .delete()
        .eq('guru_id', userRes.user.id);
      if (delErr) throw new Error(delErr.message);

      // Insertar la nueva lista (si hay)
      if (blocks.length > 0) {
        const rows = blocks.map((b) => ({
          guru_id: userRes.user!.id,
          day_of_week: b.day_of_week,
          start_time: b.start_time.length === 5 ? `${b.start_time}:00` : b.start_time,
          end_time: b.end_time.length === 5 ? `${b.end_time}:00` : b.end_time,
          timezone: b.timezone,
          is_active: true,
        }));
        const { error: insErr } = await supabase
          .from('guru_availability')
          .insert(rows);
        if (insErr) throw new Error(insErr.message);
      }
      return { count: blocks.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guru-availability'] });
      queryClient.invalidateQueries({ queryKey: ['guru-self-availability'] });
      queryClient.invalidateQueries({ queryKey: ['guru-setup-status'] });
    },
  });
}

/**
 * Reglas de booking de un Guru (públicas para que el picker pueda
 * aplicarlas al generar slots). RLS de guru_profiles permite SELECT
 * a estos campos sin exponer datos sensibles.
 */
export function useGuruBookingRules(guruId: string | undefined) {
  return useQuery({
    queryKey: ['guru-booking-rules', guruId],
    queryFn: async () => {
      if (!guruId) return null;
      const { data, error } = await supabase
        .from('guru_profiles')
        .select('min_booking_notice_hours, max_booking_horizon_days, buffer_between_sessions_min')
        .eq('user_id', guruId)
        .maybeSingle();
      if (error) throw error;
      return data as {
        min_booking_notice_hours: number;
        max_booking_horizon_days: number;
        buffer_between_sessions_min: number;
      } | null;
    },
    enabled: !!guruId,
    staleTime: 5 * 60_000,  // las rules cambian poco
  });
}

/**
 * Disponibilidad semanal de un Guru.
 */
export function useGuruAvailability(guruId: string | undefined) {
  return useQuery({
    queryKey: ['guru-availability', guruId],
    queryFn: async () => {
      if (!guruId) return [];
      const { data, error } = await supabase
        .from('guru_availability')
        .select('*')
        .eq('guru_id', guruId)
        .eq('is_active', true)
        .order('day_of_week')
        .order('start_time');
      if (error) throw error;
      return data as GuruAvailability[];
    },
    enabled: !!guruId,
  });
}

/**
 * Todas las especialidades.
 */
export function useGuruSpecialties() {
  return useQuery({
    queryKey: ['guru-specialties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guru_specialties')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as GuruSpecialty[];
    },
  });
}

/**
 * Reservas existentes del Guru en un rango (para excluir slots ocupados
 * en modo demo, sin Calendar real).
 */
export function useGuruBookingsInRange(guruId: string | undefined, fromIso: string, toIso: string) {
  return useQuery({
    queryKey: ['guru-bookings-range', guruId, fromIso, toIso],
    queryFn: async () => {
      if (!guruId) return [];
      const { data, error } = await supabase
        .from('bookings')
        .select('id, scheduled_start, scheduled_end, status')
        .eq('guru_id', guruId)
        .in('status', ['pending', 'confirmed'])
        .gte('scheduled_start', fromIso)
        .lte('scheduled_end', toIso);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!guruId,
  });
}

// =====================================================================
// Bookings — mutaciones
// =====================================================================

/**
 * Crea un booking provisional en estado 'pending'.
 * Sprint 5 lo integra con session_payments + Stripe Checkout.
 */
export function useCreateBookingDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      guru_id: string;
      scheduled_start: string;
      duration_min: number;
      timezone: string;
      purpose?: string;
    }) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');

      const start = new Date(input.scheduled_start);
      const end = new Date(start.getTime() + input.duration_min * 60_000);

      const { data, error } = await supabase
        .from('bookings')
        .insert({
          client_id: userRes.user.id,
          guru_id: input.guru_id,
          scheduled_start: start.toISOString(),
          scheduled_end: end.toISOString(),
          timezone: input.timezone,
          status: 'pending',
          purpose: input.purpose ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guru-bookings-range'] });
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
    },
  });
}

/**
 * Llama al edge function create-session-checkout para iniciar el pago de
 * una sesión 1:1. Devuelve la URL de Stripe Checkout a la cual redirigir.
 */
export function useCreateSessionCheckout() {
  return useMutation({
    mutationFn: async (input: {
      booking_id: string;
      success_url: string;
      cancel_url: string;
      // Sprint G5: opcional. Si se pasa, el server re-valida que sea del
      // user y lo aplica como descuento fijo. Si es inválido, ignora.
      coupon_code?: string;
    }): Promise<{
      url: string;
      applied_discount_pct: number;
      gross_cents: number;
      net_cents: number;
      coupon_applied?: boolean;
      coupon_value_cents?: number;
    }> => {
      const { data, error } = await supabase.functions.invoke('create-session-checkout', {
        body: input,
      });
      if (error) {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body?.code === 'booking_not_found') throw new Error('Reserva no encontrada.');
            if (body?.code === 'not_your_booking') throw new Error('Esta reserva no es tuya.');
            if (body?.code === 'invalid_status') throw new Error('La reserva ya no está disponible para pago.');
            if (body?.code === 'already_paid') throw new Error('Esta sesión ya fue pagada.');
            if (body?.code === 'amount_too_low') throw new Error('Monto inválido.');
            if (body?.code === 'self_booking_blocked') throw new Error('No puedes reservar una sesión contigo mismo.');
            if (body?.error) throw new Error(body.error);
          } catch (parseErr) {
            if (parseErr instanceof Error) throw parseErr;
          }
        }
        throw error;
      }
      if (!data?.data?.url) throw new Error('Respuesta inválida del checkout');
      return data.data;
    },
  });
}

/**
 * Mis reservas (como cliente). RLS filtra por client_id = auth.uid().
 * Los joins anidados a profiles son por FK indirecto (bookings.guru_id →
 * guru_profiles.user_id → auth.users.id ← profiles.id), así que PostgREST
 * no los resuelve automaticamente. Hacemos 2 queries y mergeamos en JS.
 */
export function useMyBookings() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: async () => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) return [];

      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('client_id', userRes.user.id)
        .order('scheduled_start', { ascending: false });
      if (error) throw error;
      if (!bookings || bookings.length === 0) return [];

      const guruIds = [...new Set(bookings.map((b) => b.guru_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, handle, avatar_url, headline')
        .in('id', guruIds);
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

      return bookings.map((b) => ({
        ...b,
        guru: profileMap.get(b.guru_id) ?? null,
      }));
    },
  });
}

/**
 * Sesiones que tengo agendadas como GURU (los clientes me reservaron).
 */
export function useMyGuruBookings() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-guru-bookings', user?.id],
    queryFn: async () => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) return [];

      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('guru_id', userRes.user.id)
        .order('scheduled_start', { ascending: false });
      if (error) throw error;
      if (!bookings || bookings.length === 0) return [];

      const clientIds = [...new Set(bookings.map((b) => b.client_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, handle, avatar_url')
        .in('id', clientIds);
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

      return bookings.map((b) => ({
        ...b,
        client: profileMap.get(b.client_id) ?? null,
      }));
    },
  });
}
